import { createAuth0Client } from '@auth0/auth0-spa-js';
import config from 'config/config';
import qs from 'query-string';

import { routerRef } from '@/util/routerRef';
import { generateUrlWithPathParams, makeApiGatewayCall } from '@/util/helper';
import WebStorage from '@/util/webStorage';

import { RegionsMap } from './regionsMap';

export const claimsKeys = {
	TENANT_ID: 'https://auth.sitecorecloud.io/claims/tenant_id',
	TENANT_NAME: 'https://auth.sitecorecloud.io/claims/tenant_name',
	ORG_ID: 'https://auth.sitecorecloud.io/claims/org_id',
	ORG_ACCOUNT_ID: 'https://auth.sitecorecloud.io/claims/org_account_id',
	ORG_DISPLAY_NAME: 'https://auth.sitecorecloud.io/claims/org_display_name',
	ORG_TYPE: 'https://auth.sitecorecloud.io/claims/org_type',
	ROLES: 'https://auth.sitecorecloud.io/claims/roles',
};

let auth0Client = null;
let regionCode = null;
let userData = null;

const persistTenantInfo = ({ organization, tenantName, tenantId, systemId }) => {
	const tenantInfo = { organization, tenantName, tenantId };

	if (systemId) {
		tenantInfo.systemId = systemId;
	}

	WebStorage.set('tenant_info', tenantInfo);

	return tenantInfo;
};

export const getRequestedTenantFromUrl = () => {
	const query = qs.parse(location.search);

	return {
		organization: query.organization || undefined,
		tenantName: query.tenantName || undefined,
		tenantId: query.tenantId || undefined,
		systemId: query.systemId || undefined,
	};
};

// Auth0 needs tenant_name to keep the existing session. The shell switcher only
// puts tenantId in the URL — clearing the name logs the user out and the shell
// shows "Continue to Forms". Same rule as master: take name from the URL when
// present (Forms env switcher), otherwise keep the stored session tenant.
const resolveAuth0TenantInfo = (requested, stored) => {
	if (requested.organization && requested.tenantName) {
		return {
			organization: requested.organization,
			tenantName: requested.tenantName,
			tenantId: requested.tenantId || stored.tenantId,
			systemId: requested.systemId,
		};
	}

	if (stored.organization || stored.tenantName) {
		return stored;
	}

	return {
		organization: requested.organization,
		tenantName: requested.tenantName,
		tenantId: requested.tenantId,
		systemId: requested.systemId,
	};
};

export const createClient = async () => {
	const tenantInfo = resolveAuth0TenantInfo(getRequestedTenantFromUrl(), WebStorage.get('tenant_info') || {});

	persistTenantInfo(tenantInfo);

	const auth0 = await createAuth0Client({
		domain: config.authentication.domain,
		clientId: config.authentication.client_id,

		authorizationParams: {
			organization_id: tenantInfo.organization,
			tenant_name: tenantInfo.tenantName,
			system_id: tenantInfo.systemId || config.authentication.xmc_system_id, // default to xmc systemId since it is not provided in the url in xmc tools page
			audience: config.authentication.audience,
			redirect_uri: window.location.origin,
		},
		useRefreshTokens: true,
		useRefreshTokensFallback: true,
	});

	auth0Client = auth0;
	return auth0;
};

export const getAuthClient = () => auth0Client;
export const getUser = () => userData;

export const getTenantAuthorizationParams = (tenantInfo = WebStorage.get('tenant_info') || {}) => {
	const authorizationParams = {
		system_id: tenantInfo.systemId || config.authentication.xmc_system_id,
	};

	if (tenantInfo.organization) {
		authorizationParams.organization_id = tenantInfo.organization;
	}

	if (tenantInfo.tenantName) {
		authorizationParams.tenant_name = tenantInfo.tenantName;
	}

	return authorizationParams;
};

export const getAccessToken = async (cacheMode = 'on') => {
	const auth = getAuthClient();

	if (!auth) {
		return '';
	}

	return auth.getTokenSilently({
		authorizationParams: getTenantAuthorizationParams(),
		cacheMode,
	});
};

const tokenMatchesTenantContext = (user, tenantInfo) => {
	if (!user || !tenantInfo) {
		return true;
	}

	if (tenantInfo.tenantId && user[claimsKeys.TENANT_ID] !== tenantInfo.tenantId) {
		return false;
	}

	if (tenantInfo.tenantName && user[claimsKeys.TENANT_NAME] !== tenantInfo.tenantName) {
		return false;
	}

	if (tenantInfo.organization && user[claimsKeys.ORG_ID] !== tenantInfo.organization) {
		return false;
	}

	return true;
};

/** localStorage can already point at the new tenant while Auth0 still serves the previous token. */
export const ensureTokenMatchesTenantContext = async (auth) => {
	const tenantInfo = WebStorage.get('tenant_info') || {};

	if (!tenantInfo.tenantId && !tenantInfo.tenantName && !tenantInfo.organization) {
		return getUser();
	}

	if (tokenMatchesTenantContext(getUser(), tenantInfo)) {
		return getUser();
	}

	try {
		await getAccessToken('off');
		userData = await auth.getUser();
		return userData;
	} catch {
		// Silent tenant switch is not always possible from the Forms iframe.
		// loginWithRedirect here is what surfaces the shell "Continue to Forms" modal.
		const user = getUser();

		if (user) {
			persistTenantInfo({
				organization: user[claimsKeys.ORG_ID],
				tenantName: user[claimsKeys.TENANT_NAME],
				tenantId: user[claimsKeys.TENANT_ID],
				systemId: tenantInfo.systemId,
			});
		}

		return user;
	}
};

export const getTenant = async (tenantId) => {
	let url = generateUrlWithPathParams(config.inventory.base, config.inventory.get_tenant, {
		tenantId,
	});

	return makeApiGatewayCall(url, 'get');
};

export const getRegionFromTenant = (tenantData) => {
	const regionLabel = tenantData.labels.Region; // deprecated
	const regionCodeLabel = tenantData.labels.RegionCode;

	if (!regionLabel && !regionCodeLabel) {
		throw new Error('Missing Region label on tenant');
	}

	// check for regionCode first
	if (regionCodeLabel) {
		return regionCodeLabel;
		// else use legacy region to resolve regionCode
	} else {
		const regionCode = RegionsMap[regionLabel];

		if (!regionCode) {
			throw new Error('Missing region code mapping for the label');
		}

		return regionCode;
	}
};
export const setRegionCode = (tenantData) => {
	regionCode = getRegionFromTenant(tenantData);
	return regionCode;
};
export const getRegionCode = () => regionCode;

export const handleRedirectCb = async (auth) => {
	const query = window.location.search;

	if (query.includes('code=') && query.includes('state=')) {
		// Process the login state
		const redir = await auth.handleRedirectCallback();

		// Use the token from the code exchange. getAccessToken('off') with
		// localStorage tenant params can hit /oauth/token and throw
		// "Redirection is not available on /oauth/token endpoint".
		await auth.getTokenSilently();
		const user = await auth.getUser();

		const requested = getRequestedTenantFromUrl();
		const tenantInfoFromLS = WebStorage.get('tenant_info');

		persistTenantInfo({
			organization: user[claimsKeys.ORG_ID],
			tenantName: user[claimsKeys.TENANT_NAME],
			tenantId: requested.tenantId || user[claimsKeys.TENANT_ID],
			systemId: tenantInfoFromLS?.systemId,
		});

		const path = redir.appState.returnTo || location.pathname;
		if (routerRef.current) routerRef.current.navigate({ to: path });
		else window.history.replaceState({}, document.title, path);

		return redir;
	}
};

export const setUserData = async (auth) => {
	await getAccessToken();
	userData = await auth.getUser();
	return userData;
};
export const checkAuthentication = async (auth) => {
	let isAuthenticated = await auth.isAuthenticated();

	if (!isAuthenticated) {
		await auth.loginWithRedirect({
			appState: { returnTo: window.location.pathname + location.search },
			authorizationParams: {
				redirect_uri: window.location.origin,
			},
		});
		return;
	}

	return isAuthenticated;
};

/**
 * Reads tenant context from the URL, resolves tenantId to tenantName when needed,
 * and persists it for navigation. Authentication stays with the main app — Forms
 * does not redirect here.
 */
export const syncRequestedTenantContext = async () => {
	const requested = getRequestedTenantFromUrl();
	const stored = WebStorage.get('tenant_info') || {};

	if (!requested.tenantId && !requested.tenantName && !requested.organization) {
		return stored;
	}

	const organization = requested.organization || stored.organization;
	const systemId = requested.systemId || stored.systemId;
	const tenantId = requested.tenantId || stored.tenantId;
	const knownNameForId = requested.tenantId && requested.tenantId === stored.tenantId ? stored.tenantName : undefined;
	let tenantName = requested.tenantName || knownNameForId;

	if (tenantId && !tenantName) {
		try {
			const { data } = await getTenant(tenantId);

			return persistTenantInfo({
				organization: organization || data.organizationId,
				tenantName: data.name,
				tenantId,
				systemId: systemId || data.systemId,
			});
		} catch {
			// Keep the stored name so Auth0 stays authenticated. Do not clear it.
			tenantName = stored.tenantName;
		}
	}

	let tenantInfo = { organization, tenantName, tenantId, systemId };
	const user = getUser();

	if (user && tenantInfo.tenantName && user[claimsKeys.TENANT_NAME] === tenantInfo.tenantName) {
		tenantInfo = {
			...tenantInfo,
			tenantId: user[claimsKeys.TENANT_ID],
			organization: tenantInfo.organization || user[claimsKeys.ORG_ID],
		};
	}

	return persistTenantInfo(tenantInfo);
};

export const resolveAndSetRegionCode = async (auth) => {
	await getAccessToken();
	const userData = await auth.getUser();
	const tenantId = userData[claimsKeys.TENANT_ID];
	const tenant = await getTenant(tenantId);
	const region = setRegionCode(tenant.data);

	return region;
};
